/**
 * Debounced single-value committer — the pure core of the settings
 * pages' save-on-change behaviour (wrapped by hooks/useDebouncedCommit).
 *
 * A continuous input (slider scrub, typing) calls set() per change; one
 * commit fires `debounceMs` after the last change, so a gesture costs one
 * write (account writes fire the TTS-regen trigger, so write frequency
 * is cost, not just chatter). flush() commits immediately (blur,
 * unmount, pagehide).
 *
 * The pending value is held until the commit settles:
 *  - success → cleared (the doc now carries it; the caller's display
 *    falls back to the doc value, already updated via latency
 *    compensation, so there's no flicker window),
 *  - failure → cleared too (the control visibly REVERTS to the saved
 *    value — "what you see is what's saved" — while the caller's save
 *    status surfaces the error),
 *  - unless a newer set() arrived mid-flight, which keeps the newer
 *    value pending for its own commit.
 *
 * WRITES ORIGINATE ONLY FROM set()/flush() — never from observation of
 * external state. That's the rule that makes the write-loop class of
 * bug (the old Voice-page auto-save disaster, #122) structurally
 * impossible: a Firestore snapshot can never trigger a write.
 */

export interface DebouncedCommitController<T> {
  /** Record a new value and (re)start the debounce timer. */
  set(v: T): void;
  /** Commit any pending value now (blur / unmount / pagehide). */
  flush(): void;
  /** True while a value is scheduled OR its commit is in flight —
   * synchronous truth, unlike a React-mirrored pending state, which lags
   * within an event-handler burst. */
  hasPending(): boolean;
  /** Cancel without committing (only for teardown after a flush). */
  dispose(): void;
}

export function createDebouncedCommit<T>(opts: {
  commit: (v: T) => Promise<void>;
  debounceMs: number;
  /** Observer for the pending value (undefined = idle). Hooks use this
   * to re-render; must not write anything. */
  onPendingChange: (pending: T | undefined) => void;
}): DebouncedCommitController<T> {
  const { commit, debounceMs, onPendingChange } = opts;
  let pending: T | undefined;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function setPending(v: T | undefined) {
    pending = v;
    onPendingChange(v);
  }

  function fire() {
    timer = null;
    if (pending === undefined) return;
    const committed = pending;
    void commit(committed)
      .catch(() => {
        // The caller's commit() is expected to surface its own error
        // state; our job is only the revert below.
      })
      .finally(() => {
        // Clear only if no newer edit arrived while the write was in
        // flight — a newer value has its own timer and must survive.
        if (Object.is(pending, committed) && timer === null) {
          setPending(undefined);
        }
      });
  }

  return {
    set(v: T) {
      setPending(v);
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(fire, debounceMs);
    },
    flush() {
      if (timer !== null) {
        clearTimeout(timer);
        fire();
      }
    },
    hasPending() {
      return pending !== undefined;
    },
    dispose() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
    },
  };
}
